"use client";

import Script from "next/script";
import { claritySnippet, gtmSnippet, onlyOnTrackingHost } from "@/lib/tracking";
import { useIsAdminRoute } from "./adminRoute";

// Every tag here takes its id as a prop, read from /admin/settings in the root
// layout (lib/trackingDb). They were NEXT_PUBLIC_* environment variables, which
// are baked in at build time: changing one meant editing .env.production and
// rebuilding, and a variable that had never been set was indistinguishable from
// a tag that had stopped working. The env vars survive as the defaults in
// lib/tracking, so an existing deployment renders exactly what it rendered
// before anyone opens the admin panel.
//
// They are client components only so they can skip /admin — see adminRoute.
// Rendering still happens on the server for every public page, so the GTM
// snippet is in the initial HTML exactly as it was.

/** GTM loader — inline snippet placed as high in the document as the App
 *  Router allows (see app/layout.tsx), per Google's install instructions. */
export function GtmScript({ id }: { id: string }) {
  const onAdmin = useIsAdminRoute();
  if (!id || onAdmin) return null;
  return (
    <script
      id="gtm-init"
      dangerouslySetInnerHTML={{ __html: onlyOnTrackingHost(gtmSnippet(id)) }}
    />
  );
}

/** GTM <noscript> iframe — rendered immediately after the opening <body>. */
export function GtmNoScript({ id }: { id: string }) {
  const onAdmin = useIsAdminRoute();
  if (!id || onAdmin) return null;
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
      />
    </noscript>
  );
}

// Direct GA4 (gtag.js) — activates only when a Measurement ID is set. GA is
// usually wired through the GTM container instead, so this stays inert unless
// someone fills the field in.
export function Analytics({ id }: { id: string }) {
  const onAdmin = useIsAdminRoute();
  if (!id || onAdmin) return null;
  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`}
        strategy="afterInteractive"
      />
      {/* gtag was a bare function declaration. Inside the host check it would
          be scoped to that block, and track() below reads window.gtag — so it
          is assigned explicitly rather than relying on top-level scope. */}
      <Script id="ga4-init" strategy="afterInteractive">
        {onlyOnTrackingHost(`
          window.dataLayer = window.dataLayer || [];
          window.gtag = function gtag(){dataLayer.push(arguments);};
          gtag('js', new Date());
          gtag('config', ${JSON.stringify(id)}, { anonymize_ip: true });
        `)}
      </Script>
    </>
  );
}

/** Fire an analytics event — via gtag when present, else the GTM dataLayer. */
export function track(event: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Record<string, unknown>[];
  };
  if (w.gtag) {
    w.gtag("event", event, params ?? {});
  } else {
    (w.dataLayer = w.dataLayer || []).push({ event, ...(params ?? {}) });
  }
}

// Microsoft Clarity — heatmaps, scroll maps and session recordings. Activates
// only when a project id is set (free at clarity.microsoft.com; the id is in
// its setup screen). The /admin/analytics dashboard links to the Clarity views
// once this is live.
//
// Note it can also be installed inside the GTM container, which is how this
// site has been running it. Two copies double-count sessions, so the admin
// panel says so next to the field.
export function ClarityScript({ id }: { id: string }) {
  const onAdmin = useIsAdminRoute();
  if (!id || onAdmin) return null;
  return (
    <Script id="ms-clarity" strategy="afterInteractive">
      {onlyOnTrackingHost(claritySnippet(id))}
    </Script>
  );
}
